import React from 'react';
import Inspection from 'utils/inspection';
import InspectionStatus from 'utils/inspection_state';
import Date from 'components/date';
import State from 'components/inspection/state';
import Result from 'components/inspection/result';
import ShowItemTableRow from 'components/item/show_item_table_row';
import InspectionFieldsListIndex from 'components/inspection_field/index';
import Inspector from 'components/inspection/inspector';
import CancelButton from 'components/inspection/button/cancel';
import EditButton from 'components/inspection/button/edit';
import ExecuteButton from 'components/inspection/button/execute';
import DeleteButton from 'components/inspection/button/delete';
import GoogleLinkButton from 'components/navigation/google_link_button';
import FileSection from 'components/file/section';
import UploadInspectionFileMutation from 'mutations/external_file/upload_inspection_file';
import { Grid, Typography, ButtonGroup, Box, Table, TableBody } from 'components/ui';
import { FormattedMessage } from 'react-intl';
import { StyledLink } from 'components/styles_components/link';
import { WhiteCard } from 'components/styles_components/card';
import { InspectionSubjectLink, PlaceLink, InspectionLink } from 'utils/link';

const ShowItem = ({ inspection, space, refetch }) => {
  const { place = {}, inspectionSubject = {} } = inspection;
  const inspectionStatUtil = InspectionStatus();

  return (
    <>
      <WhiteCard className={Inspection(inspection).htmlClass}>
        <Typography variant="h5">{inspection.name}</Typography>
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end">
            {space.inspectorRole && (
              <ButtonGroup>
                <ExecuteButton inspection={inspection} space={space} refetch={refetch} />
                <CancelButton inspection={inspection} space={space} refetch={refetch} />
              </ButtonGroup>
            )}
            </Box>
          </Grid>
        <Grid container direction="row" justifyContent="space-between" spacing={2}>
          <Table sx={{ mt:2, mb: 2 }}>
            <TableBody>
              <ShowItemTableRow label={<FormattedMessage id="inspection.number" />}>
                {inspection.number}
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspection.status" />}>
                <State status={inspection.status} />
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspection.due_date" />}>
                <Date
                  date={inspection.dueDate}
                  warning={inspection.incoming}
                  error={inspection.overdue}
                />
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspector" />}>
                <Inspector inspector={inspection.inspector} />
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspection.period" />}>
                {inspection.periodNumber}{' '}
                <FormattedMessage id={`inspection.period.${inspection.periodCycle}`} />
              </ShowItemTableRow>
              {inspection.doneAt && (
                <>
                  <ShowItemTableRow label={<FormattedMessage id="inspection.done_at" />}>
                    <Date date={inspection.doneAt} />
                  </ShowItemTableRow>
                  <ShowItemTableRow label={<FormattedMessage id="inspection.result" />}>
                    <Result result={inspection.result} />
                  </ShowItemTableRow>
                </>
              )}
              {inspection.status === inspectionStatUtil.CANCELED && (
                <ShowItemTableRow label={<FormattedMessage id="inspection.canceled_at" />}>
                  <Date date={inspection.canceledAt} />
                </ShowItemTableRow>
              )}
              {inspection.previousInspection && (
                <ShowItemTableRow label={<FormattedMessage id="inspection.previous" />}>
                  <StyledLink to={InspectionLink(inspection.previousInspection.id)}>
                    {inspection.previousInspection.name} ({inspection.previousInspection.number})
                  </StyledLink>
                </ShowItemTableRow>
              )}
              {inspection.nextInspection && (
                <ShowItemTableRow label={<FormattedMessage id="inspection.next" />}>
                  <StyledLink to={InspectionLink(inspection.nextInspection.id)}>
                    {inspection.nextInspection.name} ({inspection.nextInspection.number})
                  </StyledLink>
                </ShowItemTableRow>
              )}
              <ShowItemTableRow label={<FormattedMessage id="inspection.notes" />}>
                {inspection.notes}
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspection.field.count" />}>
                <InspectionFieldsListIndex
                  showValues={true}
                  inspectionFields={inspection.inspectionFields}
                />
              </ShowItemTableRow>
            </TableBody>
          </Table>

          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end">
            {space.adminRole && (
              <ButtonGroup>
                <EditButton inspection={inspection} space={space} refetch={refetch} />
                <DeleteButton inspection={inspection} space={space} />
              </ButtonGroup>
            )}
            </Box>
          </Grid>
        </Grid>
      </WhiteCard>

      {inspectionSubject && (
        <WhiteCard>
          <Typography variant="h5">
            <FormattedMessage id="inspection_subject" />
          </Typography>
          <Table sx={{ mt:2, mb: 2 }}>
            <TableBody>
              <ShowItemTableRow label={<FormattedMessage id="inspection_subject" />}>
                <StyledLink to={InspectionSubjectLink(inspectionSubject.id)}>
                  {inspectionSubject.name}
                </StyledLink>
              </ShowItemTableRow>
              <ShowItemTableRow label={<FormattedMessage id="inspection_subject.serial_number" />}>
                {inspectionSubject.serialNumber}
              </ShowItemTableRow>
            </TableBody>
          </Table>
        </WhiteCard>
      )}

      <WhiteCard>
        <Typography variant="h5">
          <FormattedMessage id="place" />
        </Typography>
        <Table sx={{ mt:2, mb: 2 }}>
          <TableBody>
            <ShowItemTableRow label={<FormattedMessage id="place" />}>
              <StyledLink to={PlaceLink(place.id)}>{place.name}</StyledLink>
            </ShowItemTableRow>

            <ShowItemTableRow label={<FormattedMessage id="place.street" />}>{place.street}</ShowItemTableRow>
            <ShowItemTableRow label={<FormattedMessage id="place.zip_code" />}>{place.zipCode}</ShowItemTableRow>
            <ShowItemTableRow label={<FormattedMessage id="place.city" />}>{place.city}</ShowItemTableRow>
          </TableBody>
        </Table>
        <Box display="flex" justifyContent="flex-end">
          <ButtonGroup variant="contained">
            <GoogleLinkButton place={place} />
          </ButtonGroup>
        </Box>
      </WhiteCard>

      <FileSection
        files={inspection.externalFiles}
        uploadMutation={UploadInspectionFileMutation}
        uploadVariables={{ inspectionId: inspection.id }}
        refetch={refetch}
        canUpload={space.adminRole || space.inspectorRole}
      />
    </>
  );
};

export default ShowItem;
